/**
 * /services/international &mdash; Public directory for walkers, sitters,
 * groomers, and trainers operating outside the US. Same hybrid Lead-Pass
 * model as the international transporter (Free / Pay-per-lead / Packs /
 * Pro Sub).
 *
 * Filterable by service_kind + country + city.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { DogIcon as Dog, ShieldCheckIcon as ShieldCheck, GlobeIcon as Globe, ArrowRightIcon as ArrowRight, ScissorsIcon as Scissors, HouseIcon as House, GraduationCapIcon as GraduationCap } from "@phosphor-icons/react";

const KINDS = [
  { v: "", label: "All services", icon: Globe },
  { v: "walker", label: "Walkers", icon: Dog },
  { v: "sitter", label: "Sitters & Boarding", icon: House },
  { v: "groomer", label: "Groomers", icon: Scissors },
  { v: "trainer", label: "Trainers", icon: GraduationCap },
];

export default function InternationalServicesDirectory() {
  const [search, setSearch] = useSearchParams();
  const [pros, setPros] = useState([]);
  const [country, setCountry] = useState(search.get("country") || "");
  const [city, setCity] = useState(search.get("city") || "");
  const [kind, setKind] = useState(search.get("kind") || "");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "60" });
      if (kind) p.set("service_kind", kind);
      if (country.trim()) p.set("country", country.trim());
      if (city.trim()) p.set("city", city.trim());
      const r = await api.get(`/services/international/directory?${p.toString()}`);
      setPros(r.data.pros || []);
    } finally {
      setLoading(false);
    }
  }, [kind, country, city]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    load();
    const next = new URLSearchParams();
    if (kind) next.set("kind", kind);
    if (country) next.set("country", country);
    if (city) next.set("city", city);
    setSearch(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, country, city]);

  return (
    <AppShell>
      <div
        className="max-w-6xl mx-auto px-4 py-10"
        data-testid="intl-services-directory"
      >
        {/* Hero */}
        <div className="text-[11px] tracking-[3px] text-[#8A6A00] font-bold uppercase mb-3">
          International Pro Services
        </div>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">
          Verified pros, worldwide
        </h1>
        <p className="text-[14px] text-[#5C5C5C] mt-3 leading-relaxed max-w-2xl">
          Independent walkers, sitters, groomers, and trainers operating
          outside the US. Armstrong introduces you &mdash; you arrange the
          service directly with the pro.
        </p>

        {/* Kind filter chips */}
        <div className="flex flex-wrap gap-2 mt-6">
          {KINDS.map((k) => {
            const Icon = k.icon;
            const active = (k.v || "") === (kind || "");
            return (
              <button
                key={k.v || "all"}
                type="button"
                onClick={() => setKind(k.v)}
                className={`text-[12px] h-9 px-3 rounded-full inline-flex items-center gap-1.5 border ${
                  active
                    ? "bg-ink border-ink text-white"
                    : "bg-white border-[#E5E5E5] text-ink hover:bg-[#FAFAFA]"
                }`}
                data-testid={`intl-kind-${k.v || "all"}`}
              >
                <Icon size={14} weight="duotone" /> {k.label}
              </button>
            );
          })}
        </div>

        {/* Country / city filters */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl">
          <Input
            placeholder="Country (e.g. France, Japan)"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="rounded-full h-10"
            data-testid="intl-svc-country"
          />
          <Input
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="rounded-full h-10"
            data-testid="intl-svc-city"
          />
        </div>

        {/* Apply CTA */}
        <div className="mt-5">
          <Link to="/services/international/apply">
            <Button
              variant="outline"
              className="rounded-full text-[12px] h-9 px-4"
              data-testid="intl-svc-cta-apply"
            >
              I&rsquo;m a pro &mdash; apply →
            </Button>
          </Link>
        </div>

        {/* Results */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-44 rounded-xl" />
            ))
          ) : pros.length === 0 ? (
            <EmptyState />
          ) : (
            pros.map((p) => <ProCard key={p.id} p={p} />)
          )}
        </div>

        {/* Venue footer */}
        <div className="mt-12 border-t border-[#E5D08A] pt-6 text-[11px] text-[#8B8B8B] leading-relaxed max-w-3xl">
          <strong>Important:</strong> Armstrong K9 Registry introduces
          buyers to independent international pros. We do not provide or
          verify the services listed. All money and contractual terms are
          between you and the pro. Verify credentials before paying.
        </div>
      </div>
    </AppShell>
  );
}

function ProCard({ p }) {
  return (
    <Link
      to={`/services/international/${p.slug}`}
      className="group rounded-xl border border-[#E5D08A] bg-white p-5 hover:shadow-md transition-shadow"
      data-testid={`intl-svc-card-${p.slug}`}
    >
      <div className="flex items-start gap-4">
        {p.photo_url ? (
          <img
            src={p.photo_url}
            alt={p.display_name}
            className="w-14 h-14 rounded-full object-cover border border-[#F0EAD6]"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#FBF7EC] border border-[#F0EAD6] flex items-center justify-center">
            <Dog size={28} weight="duotone" className="text-[#8A6A00]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-spectral text-xl tracking-tight truncate">
              {p.display_name}
            </h3>
            <span className="text-[10px] uppercase tracking-wider text-[#8A6A00] bg-[#FBF7EC] border border-[#E5D08A] px-1.5 py-0.5 rounded-full">
              {p.service_kind}
            </span>
            {p.verified_badge_at && (
              <span className="inline-flex items-center gap-1 text-[10px] text-[#8A6A00] font-semibold bg-[#FBF7EC] border border-[#E5D08A] px-2 py-0.5 rounded-full">
                <ShieldCheck size={12} weight="duotone" /> Verified
              </span>
            )}
          </div>
          <div className="text-[12px] text-[#6B6B6B] mt-0.5">
            <strong>{p.home_country}</strong>
            {p.years_in_business > 0 && (
              <> · {p.years_in_business} yrs</>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(p.cities_served || []).slice(0, 4).map((c) => (
              <span
                key={c}
                className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBF7EC] border border-[#F0EAD6] text-ink"
              >
                {c}
              </span>
            ))}
          </div>
          <div className="mt-3 text-[12px] text-crimson group-hover:text-ink inline-flex items-center gap-1 font-semibold">
            View profile <ArrowRight size={12} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="col-span-full text-center py-16 border border-dashed border-[#E5D08A] rounded-xl bg-[#FBF7EC]">
      <Globe size={36} weight="duotone" className="text-[#8A6A00] mx-auto mb-3" />
      <h3 className="font-spectral text-xl mb-1">No pros match yet</h3>
      <p className="text-[13px] text-[#6B6B6B] max-w-md mx-auto leading-relaxed">
        We&rsquo;re growing the global roster fast. If you&rsquo;re a pro
        in this area, apply to be listed.
      </p>
      <Link to="/services/international/apply">
        <Button
          variant="outline"
          className="rounded-full text-[12px] h-9 px-4 mt-5"
        >
          Become a verified pro
        </Button>
      </Link>
    </div>
  );
}
